import { DEFAULT_SESSION_TYPE, DELIVERABLE_FORMATS, DOC_TYPES, INDUSTRY_LABELS, LEAD_SOURCES, RATE_CATALOG, SECTION_DEFS, SESSION_TYPES, STUDIO_ADDRESS, VOLUME_TIERS } from "./constants.js";
import {
  sanitizeDeposit, cleanGroupLabel, cleanGroupNote, getDeposit, isEstimatorCatalogId, isImageAttachment, MAX_GROUP_LABEL, MAX_GROUP_NOTE,
  isSectionLocked, isSectionOn, lineGroupNotesFrom, normalizeLineGroups, proBonoPrints, quoteLineGroups, showsArraySheet, showsArrayTurnaround, stripEmDashes,
  buildDeliverablesText, buildPoliciesText, buildTermsText, turnaroundLines,
} from "./builderHelpers.js";
import { normalizeCompanyName, normalizeName, resolveClient, resolveContact } from "./identity.js";
import { isProBonoDiscount, normalizeBilling, setProBono } from "./proBono.js";
import { approvedCodeFor, BLANK_SESSION, clearLocationPatch, getTierRate, initSections, isISODateOnly, sessionHasCaptureData, sortSessions, studioPatch, validateSessionAdd } from "./utils.js";
import { readClient, readContact, writeClient, writeContact } from "./quoteParty.js";
import { readSchedule, writeSchedule } from "./quoteSchedule.js";
import { checkWordingText, moneyFingerprint, normalizePrintWording, PRINT_WORDING_KEYS, PRINT_WORDING_LABELS, wordingOf } from "./printWording.js";

/* ═══ THE FIELD TABLE (docs/quotes/ai-box-plan.md "Field list") ═══
   One entry per thing the AI box can name. The state sent to the model, the paths and
   allowed values in its prompt, the strict check of every operation, the preview's rows
   and the refusals all come from here, so there is one set of names.

   An entry's apply(q, value, action, env) returns { patch, ...notes } or { refuse }. It
   never writes anything itself. The schedule goes through writeSchedule and the client
   and contact through writeClient / writeContact (one writer per fact, quote-saving.md),
   so the box and the builder's own fields make the same change. */

const MAX_TEXT = 20000;
const INVALID = (reason) => ({ refuse: reason });
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const asText = (value, label) => {
  if (value === null || value === undefined) return { refuse: `${label} needs text.` };
  if (typeof value !== "string" && typeof value !== "number") return { refuse: `${label} needs text, and the reply sent something else.` };
  const text = stripEmDashes(String(value));
  if (text.length > MAX_TEXT) return { refuse: `${label} is longer than ${MAX_TEXT} characters, so it was not written. Nothing is cut silently.` };
  return { text };
};

const asBool = (value, action, label) => {
  if (action === "on") return { bool: true };
  if (action === "off") return { bool: false };
  if (typeof value === "boolean") return { bool: value };
  if (value === "on" || value === "true") return { bool: true };
  if (value === "off" || value === "false") return { bool: false };
  return { refuse: `${label} is on or off, and the reply sent something else.` };
};

const asDate = (value, label) => {
  const text = String(value ?? "").trim();
  const parsed = isISODateOnly(text) ? new Date(`${text}T00:00:00Z`) : null;
  const real = !!parsed && !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === text;
  if (!real) return { refuse: `${label} needs a real date written as YYYY-MM-DD, and "${value}" isn't one.` };
  return { date: text };
};

// The cleaners cut to fit, which is fine for a hand-typed field with a limit on it. Here
// the words came from a request, so losing the end of them has to be said.
const tooLong = (text, max, what) => {
  const n = String(text ?? "").replace(/\s+/g, " ").trim().length;
  return n > max ? `${what} can hold ${max} characters and this is ${n}, so nothing was changed. Ask for something shorter.` : null;
};

const plainText = (key, label, extra = {}) => ({
  path: key, label, cls: "edit", actions: ["set", "clear"], describe: "text",
  read: (q) => q?.[key] ?? "",
  apply: (q, value, action) => {
    if (action === "clear") return { patch: { [key]: "" } };
    const t = asText(value, label);
    return t.refuse ? t : { patch: { [key]: t.text } };
  },
  ...extra,
});

const enumField = (key, label, labels, extra = {}) => ({
  path: key, label, cls: "edit", actions: ["set"], describe: `one of ${Object.keys(labels).join(", ")}`, neverPrints: true,
  read: (q) => q?.[key] ?? "",
  apply: (_q, value) => {
    const wanted = String(value ?? "").trim();
    const id = labels[wanted] ? wanted : Object.keys(labels).find((k) => labels[k].toLowerCase() === wanted.toLowerCase());
    if (!id) return INVALID(`${label} can't be "${value}". It can be: ${Object.values(labels).join(", ")}.`);
    return { patch: { [key]: id } };
  },
  ...extra,
});

/* ── Why a changed field may not show on the page (conditional reasons only) ─── */

const hasLowPolyWork = (q) => (q?.lineItems || []).some((it) => it.catalogId === "lowpoly_session" || it.catalogId === "lowpoly_body");
export const printsLowPoly = (q) => hasLowPolyWork(q) && isSectionOn(q, "turnaround");

const sectionOffReason = (q, key) => (isSectionOn(q, key) ? null : `${SECTION_DEFS[key]?.label || key} is off, so this wording doesn't print. Turn the section on to print it.`);

export const sectionPrintsReason = (key) => (q) => {
  const off = sectionOffReason(q, key);
  if (off) return off;
  if (key === "fileFormats" && printsLowPoly(q)) return "File Formats doesn't print on a low poly quote.";
  if (key === "travel") {
    const stored = String(q?.sections?.travel?.text ?? "").trim();
    if (!stored || [SECTION_DEFS.travel.defaultText, SECTION_DEFS.travel.includedText].includes(stored)) {
      return "That is one of the two standard travel sentences, so the sentence that prints follows the travel lines on the quote.";
    }
  }
  if (key === "payment" && proBonoPrints(q)) return "A pro bono quote prints no payment sentence, so the Payment wording doesn't print.";
  return null;
};

const rawModelsSpotOff = (q) => {
  if (!isSectionOn(q, "turnaround")) return "The Turnaround section is off, so this doesn't print. The rate sheets' own turnaround notes still print.";
  if (!showsArrayTurnaround(q?.lineItems)) return "This quote only has LiDAR or drone work, so its own turnaround sentence prints instead and this value doesn't.";
  return null;
};

const turnaroundPrintsReason = (q) => {
  const off = rawModelsSpotOff(q);
  if (off) return off;
  const custom = wordingOf(q, "rawModels");
  if (custom === "") return "The raw models sentence is removed from this quote, so the value doesn't print.";
  if (custom) return "Custom wording prints instead, so this value doesn't show. Rewrite or reset that wording.";
  if (q?.turnaroundTBD) return "Turnaround is set to TBD, so the value doesn't print.";
  return null;
};

export const WORDING_PRINTS_REASON = {
  paymentSentence: (q) => (proBonoPrints(q) ? "A pro bono quote prints no payment sentence." : null),
  rawModels: rawModelsSpotOff,
  rawFiles: (q) => (printsLowPoly(q) ? "This sentence doesn't print on a low poly quote." : null),
  lowPolyExclusions: (q) => (printsLowPoly(q) ? null : "This sentence only prints on a low poly quote."),
  usageRights: () => null,
  proBonoNote: (q) => (proBonoPrints(q) ? null : "This line only prints on a pro bono quote with a $0 total."),
  estimateNote: (q) => (q?.docType === "estimate" ? null : "This line only prints on an Estimate."),
};

/* ── Client and contact ───────────────────────────────────────────── */

// A confirmed row writes to the CRM at save, against whichever client the quote then says.
// A change of client takes every confirmation off; a change of contact takes the contact
// rows' confirmations off (company rows keep theirs).
const clearConfirmations = (matches, { companyToo }) => (Array.isArray(matches)
  ? matches.map((m) => (m?.confirmed && (companyToo || (m?.extracted?.role || "contact") === "contact") ? { ...m, confirmed: false } : m))
  : matches);
const companyChangePatch = (q) => (Array.isArray(q?.contactMatches) && q.contactMatches.some((m) => m?.confirmed)
  ? { contactMatches: clearConfirmations(q.contactMatches, { companyToo: true }) } : {});
const contactChangePatch = (q) => (Array.isArray(q?.contactMatches) && q.contactMatches.length
  ? { contactMatches: clearConfirmations(q.contactMatches, { companyToo: false }) } : {});
const reconfirmNote = (q) => ((q?.contactMatches || []).some((m) => m?.confirmed)
  ? ["People and companies confirmed under Contacts Found need confirming again for this client, so nothing is written to the CRM until they are."]
  : undefined);

const currentClientRecord = (q, clients) => readClient(q, clients).record || (q?.clientUnlinked ? null : resolveClient({ name: q?.clientName }, clients).record);
export const isBookedQuote = (q, env) => q?.jobStatus === "booked" || q?.jobStatus === "completed" || (env?.jobs || []).some((j) => j.quoteId === q?.id && !j.deletedAt);

/** Ivin's decision (2026-11-03): any AI change of a quote's client needs the move button.
 *  Null when the code can tell it is the same client: the same CRM record, or, where
 *  either side has none, the same name once case, punctuation and legal suffix are off. */
export function clientMoveBetween(start, final, clients) {
  const from = currentClientRecord(start, clients);
  const to = currentClientRecord(final, clients);
  const same = from && to ? from.id === to.id : normalizeCompanyName(start?.clientName) === normalizeCompanyName(final?.clientName);
  if (same) return null;
  return { fromName: start?.clientName || "", fromCode: from?.clientCode || approvedCodeFor(start, from), toName: final?.clientName || "", toId: to?.id || null, toCode: to?.clientCode || approvedCodeFor(final, to) };
}

const clientNameEntry = {
  path: "clientName", label: "Client name", cls: "edit", actions: ["set", "clear"], describe: "text; an exact CRM client name links the client, any other name goes on unlinked",
  read: (q) => q?.clientName ?? "",
  apply: (q, value, action, env) => {
    // Unlinked on purpose: a save must not find the old client again from the contact's email.
    if (action === "clear") return { patch: { ...writeClient(q, { name: "" }), clientUnlinked: true, ...companyChangePatch(q) }, notes: reconfirmNote(q) };
    const t = asText(value, "Client name");
    if (t.refuse) return t;
    const name = t.text.trim();
    const match = resolveClient({ name }, env.clients).record;
    // The same client named again is no change of client, so its code and rows stay.
    if (match && currentClientRecord(q, env.clients)?.id === match.id) return { patch: { ...writeClient(q, { record: match }), approvedClientCode: q?.approvedClientCode ?? null } };
    if (match) return { patch: { ...writeClient(q, { record: match }), ...companyChangePatch(q) }, notes: reconfirmNote(q) };
    return {
      patch: { ...writeClient(q, { name }), clientUnlinked: true, ...companyChangePatch(q) },
      notes: [`${name} isn't in the CRM, so the name goes on the quote unlinked. Add them with the client picker.`, ...(reconfirmNote(q) || [])],
    };
  },
};

/* A client code means "this quote belongs to this client" (plan, decision 2). */
const clientCodeEntry = {
  path: "clientCode", label: "Client code", cls: "edit", actions: ["set"], describe: "letters and digits; a code another CRM client owns moves the quote to that client", neverPrints: true,
  // The code the client has, or the one approved on this quote; never the one booking would
  // generate for a client with none (the job dry run says that one, as what booking would do).
  read: (q, env) => currentClientRecord(q, env?.clients)?.clientCode || q?.approvedClientCode?.code || "",
  apply: (q, value, _action, env) => {
    const code = String(value ?? "").trim().toLowerCase();
    if (!/^[a-z0-9]{1,12}$/.test(code)) return INVALID(`Client code "${value}" can only hold letters and numbers.`);
    if (isBookedQuote(q, env)) return INVALID("This quote is booked. The job already has its code and ID, and the quote can't change them. Change it on the job.");
    const clients = env.clients || [];
    const owner = clients.find((c) => String(c.clientCode || "").toLowerCase() === code);
    const current = currentClientRecord(q, clients);
    if (owner) {
      // Asked of the client this reply started on: the client's name in the same reply runs
      // first and may already have moved it, and this code is then still applied, not "no effect".
      const from = currentClientRecord(env.replyStart || q, clients);
      if (from?.id === owner.id && current?.id === owner.id) return { patch: {}, noEffect: `${owner.name} already has the code ${code}.` };
      return {
        patch: { ...writeClient(q, { record: owner }), ...companyChangePatch(q) },
        notes: reconfirmNote(q),
        movesClient: from?.id !== owner.id,
      };
    }
    if (current?.clientCode) return INVALID(`${current.name} already has the code ${current.clientCode}, and ${code} belongs to no client. A typo mustn't change a client's code, so change it in the CRM.`);
    if (!current) return INVALID(`${q?.clientName || "This client"} isn't in the CRM, so there is no client to hold the code ${code}. Add them with the client picker, then set the code.`);
    return { patch: { approvedClientCode: { code, clientId: current.id, clientName: q?.clientName || "" } } };
  },
};

const contactNameEntry = {
  path: "contactName", label: "Contact name", cls: "edit", actions: ["set", "clear"], describe: "text; an exact CRM contact name links that person with their email and phone",
  read: (q) => q?.contactName ?? "",
  apply: (q, value, action, env) => {
    if (action === "clear") return { patch: { ...writeContact(q, { name: "" }), contactEntityId: null, contactEmail: "", contactPhone: "", ...contactChangePatch(q) } };
    const t = asText(value, "Contact name");
    if (t.refuse) return t;
    const name = t.text.trim();
    const match = resolveContact({ name }, env.contacts).record;
    if (match) return { patch: { ...writeContact(q, { record: match }), ...(match.id === q?.contactEntityId ? {} : contactChangePatch(q)) } };
    // Name, email and phone are one person: another name doesn't keep the last one's details.
    const samePerson = !q?.contactEntityId && normalizeName(name) === normalizeName(q?.contactName || "");
    return {
      patch: { ...writeContact(q, { name }), contactEntityId: null, contactEmail: samePerson ? (q?.contactEmail || "") : "", contactPhone: samePerson ? (q?.contactPhone || "") : "", ...contactChangePatch(q) },
      notes: [`${name} isn't in your contacts, so the name goes on the quote unlinked. A quote can't be saved until its contact is linked: add them with the contact picker.`],
    };
  },
};

const contactDetail = (key, label, change) => ({
  path: key, label, cls: "edit", actions: ["set", "clear"], describe: "text, only while the contact isn't linked", neverPrints: true,
  read: (q) => q?.[key] ?? "",
  apply: (q, value, action, env) => {
    const linked = readContact(q, env.contacts);
    if (linked.id) return INVALID(`${label} comes from the linked contact ${linked.record?.name || linked.name}, and saving replaces it from the CRM. Change it there, or unlink the contact first.`);
    if (linked.linked) return INVALID(`${label} comes from the person confirmed under Contacts Found, and saving replaces it from that record. Change it in the CRM, or undo the confirmation first.`);
    if (action === "clear") return { patch: writeContact(q, { [change]: "" }) };
    const t = asText(value, label);
    return t.refuse ? t : { patch: writeContact(q, { [change]: t.text.trim() }) };
  },
});

/* ── Schedule (lib/quoteSchedule.js is the one writer) ─────────────── */

const write = (q, change) => {
  const res = writeSchedule(q, change);
  return res.refuse ? INVALID(res.refuse) : { patch: res.patch };
};
const firstDay = (q, type = null) => {
  const days = readSchedule(q).days;
  return (type ? days.find((s) => s.sessionType === type && s.date) : null) || days[0] || null;
};
const ADDRESS_PARTS = ["locationVenue", "locationAddress", "locationCity", "locationState", "locationZip"];
const ADDRESS_LABELS = { locationVenue: "Venue", locationAddress: "Address", locationCity: "City", locationState: "State", locationZip: "Zip" };
const sessionIsProtected = (s) => sessionHasCaptureData(s) || !!s?.gcalEventId || !!s?.gcalSyncedFingerprint;

/** Everything a day holds, in words, for a preview to show before it goes. */
export function dayDetails(s) {
  const address = ADDRESS_PARTS.map((k) => s?.[k]).filter(Boolean).join(", ");
  return [
    s?.date || "No date", s?.sessionType || "", s?.dayType ? `${s.dayType} day` : "",
    s?.isStudio ? "at the Studio" : (s?.location || ""), !s?.isStudio && address ? `Address: ${address}` : "",
    s?.callTime ? `Call time: ${s.callTime}` : "", s?.wrapTime ? `Wrap time: ${s.wrapTime}` : "", s?.notes ? `Day notes: ${s.notes}` : "",
  ].filter(Boolean).join(", ");
}

/** What a day holds that a single-day quote has no field for, so it goes when multi-day
 *  goes off and this day stays (writeSchedule carries the date and location over). */
export function detailsOnlyADayHolds(s) {
  return [
    s?.sessionType && s.sessionType !== DEFAULT_SESSION_TYPE ? `${s.sessionType} day` : "",
    s?.dayType && s.dayType !== "full" ? `${s.dayType} day` : "",
    s?.callTime ? `Call time: ${s.callTime}` : "", s?.wrapTime ? `Wrap time: ${s.wrapTime}` : "", s?.notes ? `Day notes: ${s.notes}` : "",
  ].filter(Boolean).join(", ");
}

const scheduleEntries = [
  {
    path: "scanDate", label: "Capture date", cls: "edit", actions: ["set", "clear"], describe: "YYYY-MM-DD; on a multi-day quote this is the first capture day's date",
    read: (q) => (q?.multiDay ? firstDay(q, "capture")?.date || "" : (q?.scanDate === "TBD" ? "" : q?.scanDate || "")),
    printsReason: (q) => (!q?.multiDay && q?.scanDateTBD ? "Capture date is set to TBD, so the date doesn't print." : null),
    apply: (q, value, action) => {
      const d = action === "clear" ? { date: "" } : asDate(value, "Capture date");
      if (d.refuse) return d;
      if (q?.multiDay) {
        const day = firstDay(q, "capture");
        if (!day) return INVALID("This multi-day quote has no day yet. Add a day with sessions.");
        return write(q, { updateSession: { id: day.id, patch: { date: d.date } } });
      }
      const res = write(q, { scanDate: d.date });
      // A date that was just set is a date to print: TBD would hide it.
      return res.refuse || !d.date ? res : { patch: { ...res.patch, ...write(q, { tbd: false }).patch } };
    },
  },
  {
    path: "scanDateTBD", label: "Capture date TBD", cls: "edit", actions: ["on", "off"], describe: "on/off",
    read: (q) => !!q?.scanDateTBD,
    apply: (q, value, action) => { const b = asBool(value, action, "Capture date TBD"); return b.refuse ? b : write(q, { tbd: b.bool }); },
  },
  {
    path: "multiDay", label: "Multi-day", cls: "edit", actions: ["on", "off"], describe: "on/off; off keeps the first day and discards the rest",
    read: (q) => !!q?.multiDay,
    apply: (q, value, action) => {
      const b = asBool(value, action, "Multi-day");
      if (b.refuse) return b;
      if (b.bool === !!q?.multiDay) return { patch: {}, noEffect: `Multi-day is already ${b.bool ? "on" : "off"}.` };
      if (b.bool) return write(q, { multiDay: true });
      const [kept, ...extras] = sortSessions(readSchedule(q).days);
      if ([kept, ...extras].some(sessionIsProtected)) return INVALID("A day on this quote has capture data or a calendar event, and turning off multi-day would remove it, so multi-day stays on.");
      const res = write(q, { multiDay: false });
      return res.refuse ? res : { ...res, discardDays: extras };
    },
  },
  {
    path: "jobLocation", label: "Location", cls: "edit", actions: ["set", "clear"], describe: "text; on a multi-day quote this is the first day's location",
    read: (q) => q?.jobLocation ?? "",
    apply: (q, value, action) => {
      const t = action === "clear" ? { text: "" } : asText(value, "Location");
      if (t.refuse) return t;
      const text = t.text.trim();
      if (q?.multiDay) {
        const day = firstDay(q);
        if (!day) return INVALID("This multi-day quote has no day yet. Add a day with sessions.");
        const patch = text === STUDIO_ADDRESS ? studioPatch() : { ...(day.isStudio ? clearLocationPatch() : {}), location: text, isStudio: false };
        return write(q, { updateSession: { id: day.id, patch } });
      }
      if (text === STUDIO_ADDRESS) return write(q, { studio: true });
      // Leaving the Studio takes its switch with it, or booking would still use the studio's address.
      const off = q?.isStudio ? write(q, { studio: false }).patch : {};
      const res = write({ ...q, ...off }, { location: text });
      return res.refuse ? res : { patch: { ...off, ...res.patch } };
    },
  },
  {
    path: "isStudio", label: "Studio", cls: "edit", actions: ["on", "off"], describe: "on/off; on puts the location at the studio",
    read: (q) => !!q?.isStudio,
    apply: (q, value, action) => {
      const b = asBool(value, action, "Studio");
      if (b.refuse) return b;
      if (b.bool === !!q?.isStudio) return { patch: {}, noEffect: `This quote is already ${b.bool ? "at" : "away from"} the Studio.` };
      if (q?.multiDay) {
        const day = firstDay(q);
        if (!day) return INVALID("This multi-day quote has no day yet. Add a day with sessions.");
        return write(q, { toggleSessionStudio: day.id });
      }
      return write(q, { studio: b.bool });
    },
  },
  ...ADDRESS_PARTS.map((k) => ({
    path: k, label: ADDRESS_LABELS[k], cls: "edit", actions: ["set", "clear"], describe: "text, stored for the job", neverPrints: true,
    read: (q) => q?.[k] ?? "",
    apply: (q, value, action) => {
      if (q?.multiDay) return INVALID(`${ADDRESS_LABELS[k]} is set on each day of a multi-day quote, such as sessions.S1.${k}.`);
      // Booking gives a Studio quote the studio's address (quoteLocationFields), whatever is here.
      if (q?.isStudio) return INVALID(`${ADDRESS_LABELS[k]} can't change while this quote is at the Studio, because the job always gets the studio's address. Change Location or turn Studio off first.`);
      const t = action === "clear" ? { text: "" } : asText(value, ADDRESS_LABELS[k]);
      return t.refuse ? t : { patch: { [k]: t.text.trim() } };
    },
  })),
];

/* ── Holds ─────────────────────────────────────────────────────────── */

const holdsEntries = [
  {
    path: "equipmentIds", label: "Equipment", cls: "edit", actions: ["set", "add", "remove", "clear"], neverPrints: true,
    describe: (env) => `ids: ${Object.entries(env?.equipmentMap || {}).map(([id, name]) => `${id} (${name})`).join(", ") || "none loaded"}`,
    read: (q) => q?.equipmentIds || [],
    apply: (q, value, action, env) => {
      if (action === "clear") return { patch: { equipmentIds: [] } };
      const list = Array.isArray(value) ? value : [value];
      const known = env.equipmentMap ? new Set(Object.keys(env.equipmentMap)) : null;
      const bad = known ? list.filter((id) => !known.has(id)) : [];
      if (bad.length || list.some((id) => typeof id !== "string")) return INVALID(`Equipment has no unit called ${bad.join(", ") || "that"}. It has: ${[...(known || [])].join(", ")}.`);
      const cur = q?.equipmentIds || [];
      if (action === "add") return { patch: { equipmentIds: [...new Set([...cur, ...list])] } };
      if (action === "remove") return { patch: { equipmentIds: cur.filter((id) => !list.includes(id)) } };
      return { patch: { equipmentIds: list } };
    },
  },
  {
    path: "crew", label: "Tentative crew", cls: "edit", actions: ["set", "clear"], neverPrints: true,
    describe: (env) => `list of names from the roster: ${(env?.crewRoster || []).map((m) => m?.name).filter(Boolean).join(", ") || "none loaded"}`,
    read: (q) => (q?.crew || []).map((c) => c?.name || c),
    apply: (_q, value, action, env) => {
      if (action === "clear") return { patch: { crew: [] } };
      const names = Array.isArray(value) ? value : [value];
      const roster = env.crewRoster || [];
      const out = [];
      for (const name of names) {
        const hit = roster.find((m) => m?.name && String(m.name).toLowerCase() === String(name).trim().toLowerCase());
        if (!hit) return INVALID(`${name} isn't on the crew roster. It has: ${roster.map((m) => m?.name).filter(Boolean).join(", ")}.`);
        out.push({ id: hit.id, name: hit.name });
      }
      return { patch: { crew: out } };
    },
  },
  {
    path: "holdDates", label: "Hold these dates", cls: "edit", actions: ["on", "off"], describe: "on/off", neverPrints: true,
    read: (q) => !q?.holdOff,
    apply: (_q, value, action) => { const b = asBool(value, action, "Hold these dates"); return b.refuse ? b : { patch: { holdOff: !b.bool } }; },
  },
];

/* ── Money, type, turnaround ───────────────────────────────────────── */

const statusEntry = {
  path: "jobStatus", label: "Status", cls: "edit", actions: ["set"], describe: "quoted, lost or archived (booked and completed are set by the job)", neverPrints: true,
  read: (q) => q?.jobStatus ?? "quoted",
  apply: (q, value, _a, env) => {
    const v = String(value ?? "").trim().toLowerCase();
    if (v === "booked") return INVALID("Status can't be set to Booked here, because booking makes the job. Use Book Job.");
    if (v === "completed") return INVALID("Status can't be set to Completed here. Completing is done on the job.");
    if (!["quoted", "lost", "archived"].includes(v)) return INVALID(`Status can't be "${value}". It can be Quoted, Lost or Archived.`);
    if (isBookedQuote(q, env)) return INVALID("This quote is booked. Its status follows the job, so change it on the job.");
    return { patch: { jobStatus: v } };
  },
};

const docTypeEntry = {
  path: "docType", label: "Document type", cls: "edit", actions: ["set"], describe: "quote, rate_sheet or estimate (a change order can't be made or unmade here)",
  read: (q) => q?.docType ?? "quote",
  apply: (q, value) => {
    const wanted = String(value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
    const id = wanted === "quote_sheet" ? "quote" : wanted === "ratesheet" ? "rate_sheet" : wanted;
    if (q?.docType === "change_order" || q?.changeOrderOfJobId) return INVALID("This is a change order. Its type stays, so the box can't detach it from its job.");
    if (id === "change_order") return INVALID("A quote can't become a change order here. Start one from the job with Change Order.");
    if (!["quote", "rate_sheet", "estimate"].includes(id) || !DOC_TYPES[id]) return INVALID(`Document type can't be "${value}". It can be quote, rate_sheet or estimate.`);
    return { patch: { docType: id } };
  },
};

const validFormatIds = DELIVERABLE_FORMATS.map((f) => f.id);
const formatsEntry = {
  path: "deliverableFormats", label: "Deliverable formats", cls: "edit", actions: ["set", "add", "remove"], describe: `list of ids: ${validFormatIds.join(", ")}`, neverPrints: true,
  read: (q) => q?.deliverableFormats || ["obj"],
  apply: (q, value, action) => {
    const list = Array.isArray(value) ? value : [value];
    const bad = list.filter((id) => !validFormatIds.includes(id));
    if (bad.length) return INVALID(`Deliverable formats has no format called ${bad.join(", ")}. It has: ${validFormatIds.join(", ")}.`);
    const cur = q?.deliverableFormats || ["obj"];
    if (action === "add") return { patch: { deliverableFormats: [...new Set([...cur, ...list])] } };
    if (action === "remove") return { patch: { deliverableFormats: cur.filter((id) => !list.includes(id)) } };
    return { patch: { deliverableFormats: list } };
  },
};

const sectionOf = (q, key) => q?.sections?.[key] || { enabled: isSectionOn(q, key), text: SECTION_DEFS[key]?.defaultText || "" };
const withSection = (q, key, next) => ({ sections: { ...(q?.sections || initSections()), [key]: next } });

const depositEntry = {
  path: "deposit", label: "Deposit", cls: "edit", actions: ["set", "clear"], describe: `{ "type": "none" | "percent" | "amount" | "full_session", "value": number or null }`,
  read: (q) => { const d = getDeposit(q); return { type: d.type, value: d.value }; },
  printsReason: (q) => {
    if (proBonoPrints(q)) return "A pro bono quote prints no payment sentence, so the deposit doesn't print.";
    const custom = wordingOf(q, "paymentSentence");
    if (custom === "") return "The payment sentence is removed from this quote, so the deposit doesn't print.";
    if (custom && !custom.includes("{deposit}")) return "Custom payment wording prints instead, so this deposit doesn't show. Rewrite or reset that wording.";
    return null;
  },
  apply: (q, value, action) => {
    // Saves merge nested maps, so an old legacy:true is written off or the old sentence prints after a reload.
    const offLegacy = q?.deposit && "legacy" in q.deposit ? { legacy: false } : {};
    if (action === "clear") return { patch: { deposit: { type: "none", value: null, ...offLegacy } } };
    const parsed = value && typeof value === "object" ? sanitizeDeposit(value.type, value.value) : null;
    if (!parsed) return INVALID("Deposit needs a type of none, percent, amount or full_session, with a percent from 1 to 100 or an amount above 0.");
    const dep = { ...parsed, ...offLegacy };
    // A deposit needs its cancellation clause, and that section may have been emptied.
    if (dep.type !== "none" && !String(sectionOf(q, "cancellation").text ?? "").trim()) {
      return { patch: { deposit: dep, ...withSection(q, "cancellation", { ...sectionOf(q, "cancellation"), enabled: true, text: SECTION_DEFS.cancellation?.defaultText || "" }) }, notes: ["A deposit needs the Cancellation section, which was empty, so its standard wording is back."] };
    }
    return { patch: { deposit: dep } };
  },
};

const billingEntry = {
  path: "billing", label: "Billing", cls: "edit", actions: ["set"], describe: "standard or pro_bono (pro bono adds the Pro bono discount itself)",
  read: (q) => normalizeBilling(q?.billing),
  apply: (q, value) => {
    if (value !== "pro_bono" && value !== "standard") return INVALID(`Billing can be standard or pro_bono, not "${value}".`);
    if (value === "standard" && normalizeBilling(q?.billing) === "standard") return { patch: {}, noEffect: "Billing is already standard." };
    return { patch: setProBono(q, value === "pro_bono") };
  },
};

const pricingSheetEntry = {
  path: "includePricingSheet", label: "Camera array sheet", cls: "edit", actions: ["on", "off", "reset"], describe: "on/off/reset (reset = automatic)",
  read: (q) => (q?.includePricingSheet == null ? "auto" : !!q.includePricingSheet),
  apply: (q, value, action) => {
    if (action === "reset") return { patch: { includePricingSheet: null } };
    const b = asBool(value, action, "Camera array sheet");
    if (b.refuse) return b;
    // The print rule stays as it is on every saved quote, so a switch that can't print says so.
    if (b.bool && !showsArraySheet({ ...q, includePricingSheet: true })) return INVALID("Camera array sheet only prints on a quote with a studio session, an on-location day, or a head or body scan. This quote has none, so nothing was changed.");
    return { patch: { includePricingSheet: b.bool } };
  },
};

/* Turnaround from free words: a value plus its unit, so "2 weeks" can't print "2 weeks weeks". */
export function turnaroundPatch(raw) {
  if (raw && typeof raw === "object") {
    if (raw.tbd === true) return { turnaroundTBD: true };
    return turnaroundPatch(`${raw.value ?? ""} ${raw.unit ?? ""}`);
  }
  const text = String(raw ?? "").trim().toLowerCase().replace(/[–—]/g, "-");
  if (!text) return { refuse: "Turnaround needs a value, such as 3-5 business days or 2 weeks." };
  if (text === "tbd") return { turnaroundTBD: true };
  const m = text.match(/^(\d+(?:\.\d+)?(?:\s*(?:-|to)\s*\d+(?:\.\d+)?)?)\s*(business days?|days?|weeks?|wks?)?$/);
  if (!m) return { refuse: `Turnaround "${raw}" isn't a value and unit, such as 3-5 business days or 2 weeks.` };
  return { turnaround: m[1].replace(/\s*(?:-|to)\s*/, "-"), turnaroundUnit: /^w/.test(m[2] || "") ? "weeks" : "businessDays", turnaroundTBD: false };
}

const turnaroundEntry = {
  path: "turnaround", label: "Turnaround", cls: "edit", actions: ["set", "reset"], describe: `text such as "3-5 business days", "2 weeks" or "TBD"`,
  read: (q) => (q?.turnaroundTBD ? "TBD" : `${q?.turnaround || "3-5"} ${q?.turnaroundUnit === "weeks" ? "weeks" : "business days"}`),
  printsReason: turnaroundPrintsReason,
  apply: (_q, value, action) => {
    if (action === "reset") return { patch: { turnaround: "3-5", turnaroundUnit: "businessDays", turnaroundTBD: false } };
    const patch = turnaroundPatch(value);
    return patch.refuse ? patch : { patch };
  },
};

export const QUOTE_LEVEL_FIELDS = [
  plainText("summary", "Summary"),
  plainText("deliverableText", "Deliverable text"),
  formatsEntry,
  docTypeEntry,
  clientNameEntry, clientCodeEntry, contactNameEntry, contactDetail("contactEmail", "Contact email", "email"), contactDetail("contactPhone", "Contact phone", "phone"),
  plainText("productionName", "Production name", { neverPrints: true }),
  plainText("internalNotes", "Internal notes", { neverPrints: true }),
  plainText("referredBy", "Referred by", { neverPrints: true }),
  enumField("industry", "Industry", INDUSTRY_LABELS),
  enumField("leadSource", "Lead source", LEAD_SOURCES),
  statusEntry,
  // Set only: a save fills an empty quote date with today, so "cleared" wouldn't stay.
  { path: "quoteDate", label: "Quote date", cls: "edit", actions: ["set"], describe: "YYYY-MM-DD", read: (q) => q?.quoteDate ?? "", apply: (_q, value) => { const d = asDate(value, "Quote date"); return d.refuse ? d : { patch: { quoteDate: d.date } }; } },
  { path: "followUpDate", label: "Follow-up date", cls: "edit", actions: ["set", "clear"], describe: "YYYY-MM-DD", neverPrints: true, read: (q) => q?.followUpDate ?? "", apply: (_q, value, action) => { if (action === "clear") return { patch: { followUpDate: "" } }; const d = asDate(value, "Follow-up date"); return d.refuse ? d : { patch: { followUpDate: d.date } }; } },
  depositEntry, billingEntry, pricingSheetEntry, turnaroundEntry,
  { path: "turnaroundTBD", label: "Turnaround TBD", cls: "edit", actions: ["on", "off"], describe: "on/off", read: (q) => !!q?.turnaroundTBD, apply: (_q, v, a) => { const b = asBool(v, a, "Turnaround TBD"); return b.refuse ? b : { patch: { turnaroundTBD: b.bool } }; } },
  ...scheduleEntries,
  ...holdsEntries,
];

export const fieldByPath = new Map(QUOTE_LEVEL_FIELDS.map((f) => [f.path, f]));

/* ── Sections ─────────────────────────────────────────────────────── */

export const SECTION_KEYS = Object.keys(SECTION_DEFS);
const lockedReason = (q, key) => (SECTION_DEFS[key]?.alwaysOn
  ? `${SECTION_DEFS[key].label} always prints, so it can't be turned off.`
  : `${SECTION_DEFS[key]?.label} stays on while the quote has a deposit.`);

function applySectionText(q, key, value, action) {
  const def = SECTION_DEFS[key];
  if (def?.generated) return INVALID("The Turnaround section is written by the tool from the turnaround value, so it has no wording to edit.");
  const cur = sectionOf(q, key);
  if (action === "reset") return { patch: withSection(q, key, { ...cur, text: def?.defaultText || "" }) };
  const t = action === "clear" ? { text: "" } : asText(value, `${def?.label} wording`);
  if (t.refuse) return t;
  if (!t.text.trim()) {
    if (isSectionLocked(q, key)) return INVALID(`${lockedReason(q, key)} Its wording can't be empty, so use reset to go back to the standard wording.`);
    return { patch: withSection(q, key, { ...cur, enabled: false, text: "", ...(key === "travel" ? { operatorSet: true } : {}) }) };
  }
  return { patch: withSection(q, key, { ...cur, text: t.text }) };
}

function applySectionSwitch(q, key, value, action) {
  const def = SECTION_DEFS[key];
  const b = asBool(value, action, `${def?.label} section`);
  if (b.refuse) return b;
  if (isSectionLocked(q, key) && !b.bool) return INVALID(lockedReason(q, key));
  // A travel switch set here is the operator's choice, so a later schedule edit doesn't undo it.
  return { patch: withSection(q, key, { ...sectionOf(q, key), enabled: b.bool, ...(key === "travel" ? { operatorSet: true } : {}) }) };
}

/* ── Lines ────────────────────────────────────────────────────────── */

const ALL_CATALOG = () => Object.values(RATE_CATALOG).flat();
const catalogItem = (id) => ALL_CATALOG().find((c) => c.id === id) || null;
export const FROZEN_LINE_REASON = "LiDAR and drone lines are priced by their estimators, so the box can't change, move or remove them. Use the estimator.";
const LINE_ADD_KEYS = ["catalogId", "description", "notes", "qty", "rate", "discount", "group"];
const DISCOUNT_KEYS = ["name", "type", "value", "reason"];
const BLOCK_ADD_KEYS = ["label", "note"];
const unknownKey = (value, allowed, what) => {
  const k = value && typeof value === "object" ? Object.keys(value).find((x) => !allowed.includes(x)) : null;
  return k ? `${what} can't take ${k}, so nothing was changed. It takes ${allowed.join(", ")}.` : null;
};

function validDiscount(value, label) {
  if (!value || typeof value !== "object") return { refuse: `${label} needs a name, a type of % or $, and a value.` };
  const type = value.type === "percent" ? "%" : value.type === "amount" || value.type === "fixed" ? "$" : value.type;
  if (type !== "%" && type !== "$") return { refuse: `${label} type has to be % or $, not "${value.type}".` };
  const v = Number(value.value);
  if (!Number.isFinite(v) || v < 0) return { refuse: `${label} value can't be negative or empty.` };
  if (type === "%" && v > 100) return { refuse: `${label} can't be more than 100 percent.` };
  return { discount: { name: stripEmDashes(String(value.name ?? "")), type, value: v, reason: stripEmDashes(String(value.reason ?? "")) } };
}
// "10" on a line means 10 percent off; "$50" means fifty dollars off each.
const lineDiscountFrom = (value, label) => {
  if (typeof value === "number" || (typeof value === "string" && /^\$?\s*\d/.test(value.trim()))) {
    const s = String(value).trim();
    const dollars = s.startsWith("$");
    const n = Number(s.replace(/[$%\s]/g, ""));
    return validDiscount({ name: "", type: dollars ? "$" : "%", value: n, reason: "" }, label);
  }
  const extra = unknownKey(value, DISCOUNT_KEYS, label);
  return extra ? { refuse: extra } : validDiscount(value, label);
};

const parseQty = (value, q) => {
  if (value === null || value === "") return q?.docType === "rate_sheet" ? { qty: null } : { refuse: "Quantity needs a number on anything but a rate sheet." };
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return { refuse: `Quantity can't be "${value}".` };
  return { qty: n };
};
const parseRate = (value) => {
  const n = Number(typeof value === "string" ? value.replace(/[$,\s]/g, "") : value);
  if (value === null || value === "" || value === undefined || !Number.isFinite(n) || n < 0) return { refuse: `Rate needs a number of 0 or more, and "${value}" isn't one.` };
  return { rate: n };
};
const isTiered = (id) => !!catalogItem(id)?.tiered;

export const LINE_FIELDS = {
  description: { label: "Line description", actions: ["set"], read: (li) => li?.description ?? "" },
  notes: { label: "Line note", actions: ["set", "clear"], read: (li) => li?.notes ?? "", printsReason: (li) => (li?.discount && li.discount.value > 0 ? "A discounted line prints its discount where a note would go, so this note doesn't print." : null) },
  qty: { label: "Quantity", actions: ["set"], read: (li) => li?.qty ?? null },
  rate: { label: "Rate", actions: ["set"], read: (li) => Number(li?.rate) || 0 },
  discount: { label: "Line discount", actions: ["set", "clear"], read: (li) => li?.discount ?? null },
  group: { label: "Block", actions: ["set", "clear"], read: (li) => cleanGroupLabel(li?.group) },
};

function applyLinePatch(q, li, field, value, action) {
  const label = LINE_FIELDS[field].label;
  if (field === "description") {
    const t = asText(value, label);
    if (t.refuse) return t;
    if (!t.text.trim()) return INVALID("Line description can't be empty. To take a line off, remove it.");
    return { line: { description: t.text } };
  }
  if (field === "notes") {
    if (action === "clear") return { line: { notes: "" } };
    const t = asText(value, label);
    return t.refuse ? t : { line: { notes: t.text } };
  }
  if (field === "qty") {
    const p = parseQty(value, q);
    if (p.refuse) return p;
    const next = { qty: p.qty };
    // Head and body scans price from their volume tiers.
    if (isTiered(li.catalogId) && p.qty > 0) next.rate = getTierRate(li.catalogId, p.qty);
    return { line: next };
  }
  if (field === "rate") {
    if (isTiered(li.catalogId)) return INVALID("Head and body scans price from their volume tiers, so their rate can't be typed. Change the quantity.");
    const p = parseRate(value);
    return p.refuse ? p : { line: { rate: p.rate, isCustomRate: true } };
  }
  if (field === "discount") {
    if (action === "clear") return { line: { discount: null } };
    const d = lineDiscountFrom(value, label);
    return d.refuse ? d : { line: { discount: d.discount } };
  }
  if (field === "group") {
    if (action === "clear") return { line: { group: "" } };
    const long = tooLong(value, MAX_GROUP_LABEL, "A block name");
    if (long) return INVALID(long);
    const g = cleanGroupLabel(value);
    if (!g) return INVALID("Block needs a name.");
    return { line: { group: g } };
  }
  return INVALID(`${label} can't be changed.`);
}

const newKey = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

export function newLineFrom(value, q) {
  if (!value || typeof value !== "object") return INVALID("A new line needs a description, a quantity and a rate.");
  const extra = unknownKey(value, LINE_ADD_KEYS, "A new line") || (value.discount && typeof value.discount === "object" ? unknownKey(value.discount, DISCOUNT_KEYS, "A new line's discount") : null);
  if (extra) return INVALID(extra);
  // "custom" is how the state shows a line with no catalog item.
  const catalogId = value.catalogId && value.catalogId !== "custom" ? value.catalogId : null;
  let base = { catalogId: null, description: "", notes: "", qty: 1, rate: null, discount: null };
  if (catalogId) {
    if (isEstimatorCatalogId(catalogId)) return INVALID(FROZEN_LINE_REASON);
    const item = catalogItem(catalogId);
    if (!item) return INVALID(`The rate catalog has no item called ${catalogId}.`);
    base = { ...base, catalogId, description: item.label, notes: item.notes || "", rate: item.tiered ? null : item.rate };
  }
  if (value.description !== undefined) {
    const t = asText(value.description, "Line description");
    if (t.refuse) return t;
    base.description = t.text;
  }
  if (!String(base.description).trim()) return INVALID("A new line needs a description.");
  if (value.notes !== undefined && value.notes !== null) {
    const t = asText(value.notes, "Line note");
    if (t.refuse) return t;
    base.notes = t.text;
  }
  if (value.qty !== undefined) {
    const p = parseQty(value.qty, q);
    if (p.refuse) return p;
    base.qty = p.qty;
  } else if (q?.docType === "rate_sheet") base.qty = null;
  if (base.catalogId && isTiered(base.catalogId)) {
    base.rate = getTierRate(base.catalogId, base.qty || 1);
    if (value.rate !== undefined && value.rate !== null && Number(value.rate) !== base.rate) {
      return INVALID(`Head and body scans price from their volume tiers, so ${base.description} at this quantity is ${base.rate} each and can't be typed as ${value.rate}. Add a custom line for a different price.`);
    }
  } else if (value.rate !== undefined && value.rate !== null) {
    const p = parseRate(value.rate);
    if (p.refuse) return p;
    base.rate = p.rate;
    if (base.catalogId && p.rate !== catalogItem(base.catalogId)?.rate) base.isCustomRate = true;
  } else if (base.rate === null) {
    return INVALID(`The new line "${base.description}" has no rate, and a custom line can't guess one. Say the rate, or name a catalog item.`);
  }
  if (value.discount) {
    const d = lineDiscountFrom(value.discount, "Line discount");
    if (d.refuse) return d;
    base.discount = d.discount;
  }
  const long = tooLong(value.group, MAX_GROUP_LABEL, "A block name");
  if (long) return INVALID(long);
  const group = cleanGroupLabel(value.group);
  return { line: { key: newKey("li"), ...base, ...(group ? { group } : {}) } };
}

/* ── Discounts, blocks, days, attachments: their fields ────────────── */

export const DISCOUNT_FIELDS = { name: "Discount name", type: "Discount type", value: "Discount value", reason: "Discount reason" };
const PRO_BONO_DISCOUNT_REASON = "The Pro bono discount follows the billing switch. Set billing to change it.";
export const BLOCK_FIELDS = { note: "Block note", printLabel: "Block prints as", order: "Block order" };
export const SESSION_TEXT = {
  callTime: "Call time", wrapTime: "Wrap time", notes: "Day notes", location: "Day location",
  locationVenue: "Day venue", locationAddress: "Day address", locationCity: "Day city", locationState: "Day state", locationZip: "Day zip",
};
const SESSION_PROTECTED = new Set(["id", "captureLog", "captureNotes", "issues", "actualArrival", "actualWrap", "travelTime", "peoplePresent", "clientContacts", "gcalEventId", "gcalSyncedFingerprint"]);
const SESSION_TIMES = new Set(["callTime", "wrapTime"]);
const DAY_TYPES = ["full", "partial"];
const dayTypeOf = (v) => (v === "half" ? "partial" : v);
const ATTACHMENT_PROTECTED = new Set(["id", "url", "storagePath", "size", "type", "aiRead", "extracted", "dataUrl", "file", "bytes"]);

// Call and wrap times go into calendar events as HH:mm. "9 AM" is how people say it.
const asTime = (value, label) => {
  const text = String(value ?? "").trim();
  if (!text) return { text: "" };
  const m = text.match(/^(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m?\.?$/i) || text.match(/^(\d{1,2}):(\d{2})$/);
  const minutes = m ? Number(m[2] || 0) : 0;
  let hours = m ? Number(m[1]) : NaN;
  const half = m?.[3]?.toLowerCase();
  if (half && hours >= 1 && hours <= 12) hours = (hours % 12) + (half === "p" ? 12 : 0);
  else if (half) hours = NaN;
  if (!Number.isInteger(hours) || hours > 23 || minutes > 59) return { refuse: `${label} has to be a time such as 09:00 or 9 AM, not "${value}".` };
  return { text: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}` };
};

/* ── Keys that aren't edited, and why ─────────────────────────────── */

export const OTHER_KEYS = {
  id: ["protected", "The quote number is set by the tool."],
  createdAt: ["protected", "Created and saved dates are written by the tool."],
  savedAt: ["protected", "Created and saved dates are written by the tool."],
  updatedAt: ["protected", "Created and saved dates are written by the tool."],
  deletedAt: ["protected", "Deleting a quote is done from Saved Quotes."],
  versions: ["protected", "Version history is written by the tool."],
  followUpHistory: ["protected", "Follow-up history is written by the tool."],
  draft: ["protected", "Draft is set by Save Draft and Ingest."],
  step: ["protected", "The builder's step isn't quote content."],
  mathVerified: ["protected", "This is set by the tool on save."],
  _docId: ["protected", "This is set by the database."],
  emailText: ["protected", "The original email stays as it came in."],
  notesText: ["protected", "The notes stay as they were written."],
  notesSource: ["protected", "Who wrote the notes is set by the tool."],
  lidarDetected: ["protected", "LiDAR facts belong to the estimator."],
  lidarPrefill: ["protected", "LiDAR facts belong to the estimator."],
  sourceProspectId: ["protected", "The inquiry a quote came from can't change."],
  changeOrderOfJobId: ["protected", "A change order's links can't change, so the box can't detach it from its job."],
  changeOrderOfQuoteId: ["protected", "A change order's links can't change, so the box can't detach it from its job."],
  fromAssessment: ["protected", "Where a quote came from can't change."],
  amendPulledJobData: ["protected", "This is set by Amend."],
  approvedClientCode: ["protected", "Set clientCode instead, by its rules."],
  clientUnlinked: ["protected", "This follows the client name."],
  companyEntityId: ["protected", "Set clientName instead: it links by exact CRM match."],
  clientId: ["protected", "Set clientName instead: it links by exact CRM match."],
  contactEntityId: ["protected", "Set contactName instead: it links by exact CRM match."],
  letterhead: ["protected", "The letterhead is fixed."],
  footer: ["protected", "The footer is fixed."],
  rateSheets: ["protected", "The rate sheets are fixed."],
  subtotal: ["derived", "Subtotal is computed from the lines."],
  total: ["derived", "Total is computed from the lines and discounts."],
  totalSavings: ["derived", "Savings is computed from the discounts."],
  lineGroupNotes: ["derived", "Block notes are set on each block."],
  requiresDeposit: ["legacy", "This is an old field. Set deposit instead."],
  equipment: ["legacy", "This is an old field. Set equipmentIds instead."],
  selectedEquipment: ["legacy", "This is an old field. Set equipmentIds instead."],
  aiNotes: ["transient", "The AI's note is shown in the preview and never written."],
  aiQuestions: ["transient", "This isn't quote content."],
  contactMatches: ["transient", "Contact rows are decided in the builder's contact panel."],
  savedToDb: ["transient", "This isn't quote content."],
  lineItems: ["edit", "Name a line, such as lines.L1.qty."],
  lineGroups: ["edit", "Name a block, such as blocks.B1.note."],
  quoteDiscounts: ["edit", "Name a discount, such as discounts.D1.value."],
  sessions: ["edit", "Name a day, such as sessions.S1.date."],
  attachments: ["edit", "Name an attachment, such as attachments.A1.caption."],
  sections: ["edit", "Name a section, such as sections.projectTerms.text."],
  holdOff: ["edit", "Set holdDates instead."],
  printWording: ["edit", "Name a sentence, such as printWording.rawFiles."],
  printWordingMoneyKey: ["derived", "This is computed from the money on the quote."],
  turnaroundUnit: ["edit", "Set turnaround instead, with its unit."],
};
export const LINE_OTHER_KEYS = { catalogId: "protected", key: "transient", isCustomRate: "transient", savedToDb: "transient", lidar: "protected", drone: "protected" };
export const lineKeyClass = (k) => (LINE_FIELDS[k] ? "edit" : LINE_OTHER_KEYS[k] || null);
export const sessionKeyClass = (k) => (SESSION_PROTECTED.has(k) ? "protected" : (SESSION_TEXT[k] || ["date", "sessionType", "dayType", "isStudio"].includes(k)) ? "edit" : null);
export const attachmentKeyClass = (k) => (ATTACHMENT_PROTECTED.has(k) ? "protected" : ["caption", "name", "includeInAddendum"].includes(k) ? "edit" : null);
export const quoteKeyClass = (k) => fieldByPath.get(k)?.cls || OTHER_KEYS[k]?.[0] || (k.startsWith("_") ? "transient" : null);

/* ── Request-scoped ids ───────────────────────────────────────────── */

const sessionsOf = (q) => (q?.multiDay ? readSchedule(q).days : []);

/** The ids for this quote's records, fixed before call 1 and never rebuilt. */
export function buildIdMap(q) {
  const ids = (list, prefix) => (list || []).map((_, i) => `${prefix}${i + 1}`);
  return {
    lines: ids(q?.lineItems, "L"),
    discounts: ids(q?.quoteDiscounts, "D"),
    sessions: sessionsOf(q).map((s, i) => ({ id: `S${i + 1}`, sessionId: s.id })),
    blocks: quoteLineGroups(q).map((g, i) => ({ id: `B${i + 1}`, label: g.label })),
    attachments: (q?.attachments || []).map((a, i) => ({ id: `A${i + 1}`, attachmentId: a.id })),
  };
}

/** What the model sees. The same names as the paths in its reply. */
export function buildBoxState(q, env = {}) {
  const map = buildIdMap(q);
  const sections = {};
  for (const key of SECTION_KEYS) {
    const def = SECTION_DEFS[key];
    sections[key] = { on: isSectionOn(q, key), locked: isSectionLocked(q, key), ...(def.generated ? {} : { text: q?.sections?.[key]?.text ?? def.defaultText ?? "" }) };
  }
  const top = {};
  for (const f of QUOTE_LEVEL_FIELDS) top[f.path] = f.read(q, env);
  return {
    ...top,
    lines: (q?.lineItems || []).map((li, i) => ({
      id: map.lines[i], catalogId: li.catalogId || "custom", description: li.description || "", notes: li.notes || "",
      qty: li.qty ?? null, rate: Number(li.rate) || 0, discount: li.discount || null, group: cleanGroupLabel(li.group),
      ...(isEstimatorCatalogId(li.catalogId) ? { frozen: true } : {}),
    })),
    discounts: (q?.quoteDiscounts || []).map((d, i) => ({ id: map.discounts[i], name: d.name || "", type: d.type || "%", value: Number(d.value) || 0, reason: d.reason || "" })),
    blocks: quoteLineGroups(q).map((g, i) => ({ id: map.blocks[i].id, label: g.label, note: g.note || "", ...(g.printLabel ? { printLabel: g.printLabel } : {}) })),
    sessions: sessionsOf(q).map((s, i) => ({
      id: map.sessions[i].id, date: s.date || "", sessionType: s.sessionType || "", dayType: s.dayType || "", callTime: s.callTime || "", wrapTime: s.wrapTime || "",
      notes: s.notes || "", location: s.location || "", locationVenue: s.locationVenue || "", locationAddress: s.locationAddress || "",
      locationCity: s.locationCity || "", locationState: s.locationState || "", locationZip: s.locationZip || "", isStudio: !!s.isStudio,
    })),
    attachments: (q?.attachments || []).map((a, i) => ({ id: map.attachments[i].id, name: a.name || "", caption: a.caption || "", includeInAddendum: !!a.includeInAddendum })),
    sections,
    printWording: normalizePrintWording(q?.printWording),
    printed: { deliverables: buildDeliverablesText(q), turnaround: turnaroundLines(q), terms: buildTermsText(q), policies: buildPoliciesText(q) },
  };
}

/** Every path the model may name, with its actions and allowed values. From the table. */
export function describeFields(env = {}) {
  const money = (n) => `$${Number(n).toLocaleString("en-US")}`;
  const catalog = ALL_CATALOG().filter((c) => !isEstimatorCatalogId(c.id)).map((c) => `${c.id} (${c.label}${c.tiered ? `, priced by volume tier: ${(VOLUME_TIERS[c.id] || []).map((t) => `${t.min}${t.max === Infinity ? "+" : t.max === t.min ? "" : `-${t.max}`}=${money(t.rate)}`).join(", ")}` : `, ${money(c.rate)}`})`);
  const d = (f) => (typeof f.describe === "function" ? f.describe(env) : f.describe || "");
  return [
    ...QUOTE_LEVEL_FIELDS.map((f) => `${f.path} (${f.actions.join("/")})${d(f) ? `: ${d(f)}` : ""}`),
    `lines (add, value { catalogId?, description?, notes?, qty, rate?, discount?, group? }): a catalog id fills the description, note and rate; a line with no catalog id needs a description and a rate. Catalog: ${catalog.join("; ")}`,
    `lines.L1 (remove)`,
    ...Object.entries(LINE_FIELDS).map(([k, f]) => `lines.L1.${k} (${f.actions.join("/")})${k === "discount" ? `: a number is a percent off, "$50" is dollars off each, or { name, type: "%" | "$", value, reason }` : k === "group" ? ": the block the line prints under" : ""}`),
    `discounts (add, value { name, type: "%" | "$", value, reason }): a discount on the whole quote; the name prints`,
    `discounts.D1 (remove)`,
    ...Object.keys(DISCOUNT_FIELDS).map((k) => `discounts.D1.${k} (set)`),
    `blocks (add, value { label, note }): a heading lines print under`,
    `blocks.B1 (remove: takes off the heading, its lines stay with no block)`,
    `blocks.B1.printLabel (set/clear): what the heading prints; to rename a block, set this`,
    `blocks.B1.note (set/clear), blocks.B1.order (set: position from 1)`,
    `sessions (add, value { date (YYYY-MM-DD, or "" when no date is given yet, which prints as TBD), sessionType?: ${SESSION_TYPES.join("|")}, dayType?: full|partial, isStudio?, ${Object.keys(SESSION_TEXT).join(", ")} }): only on a multi-day quote`,
    `sessions.S1 (remove)`,
    `sessions.S1.date (set/clear; clear leaves the day to be scheduled, which prints as TBD)`,
    `sessions.S1.sessionType (set: ${SESSION_TYPES.join("|")}), sessions.S1.dayType (set: full|partial)`,
    `sessions.S1.${Object.keys(SESSION_TEXT).join(", sessions.S1.")} (set/clear; callTime and wrapTime as HH:mm)`,
    `sessions.S1.isStudio (on/off)`,
    `attachments.A1.caption, attachments.A1.name (set)`,
    `attachments.A1.includeInAddendum (on/off): whether a picture prints`,
    ...PRINT_WORDING_KEYS.map((k) => `printWording.${k} (set/clear/reset): ${PRINT_WORDING_LABELS[k]}`),
    ...SECTION_KEYS.filter((k) => !SECTION_DEFS[k].generated).map((k) => `sections.${k}.text (set/clear/reset): ${SECTION_DEFS[k].label} wording`),
    ...SECTION_KEYS.map((k) => `sections.${k}.on (on/off)${SECTION_DEFS[k].alwaysOn ? ": always on" : ""}`),
  ];
}

/* ── Resolving a path ─────────────────────────────────────────────── */

export function resolvePath(path) {
  if (typeof path !== "string" || !path.trim()) return { refuse: "The reply named no field." };
  const parts = path.trim().split(".");
  const head = parts[0];
  if (fieldByPath.has(path)) return { kind: "top", entry: fieldByPath.get(path) };
  if (head === "lines") {
    if (parts.length === 1) return { kind: "linesAdd" };
    if (parts.length === 2) return { kind: "line", id: parts[1] };
    if (parts.length === 3 && LINE_FIELDS[parts[2]]) return { kind: "lineField", id: parts[1], field: parts[2] };
    if (parts.length === 3 && parts[2] === "catalogId") return { refuse: "A line's catalog id stays as it is. Add a new line to change what it is." };
    return { refuse: `A line has no field called ${parts.slice(2).join(".")}. It has ${Object.keys(LINE_FIELDS).join(", ")}.` };
  }
  if (head === "discounts") {
    if (parts.length === 1) return { kind: "discountsAdd" };
    if (parts.length === 2) return { kind: "discount", id: parts[1] };
    if (parts.length === 3 && DISCOUNT_FIELDS[parts[2]]) return { kind: "discountField", id: parts[1], field: parts[2] };
    return { refuse: `A discount has no field called ${parts.slice(2).join(".")}.` };
  }
  if (head === "blocks") {
    if (parts.length === 1) return { kind: "blocksAdd" };
    if (parts.length === 2) return { kind: "block", id: parts[1] };
    if (parts.length === 3 && parts[2] === "label") return { refuse: "A block's label is what ties its lines together, so it stays as it is. Set printLabel to change what prints." };
    if (parts.length === 3 && BLOCK_FIELDS[parts[2]]) return { kind: "blockField", id: parts[1], field: parts[2] };
    return { refuse: `A block has no field called ${parts.slice(2).join(".")}.` };
  }
  if (head === "sessions") {
    if (parts.length === 1) return { kind: "sessionsAdd" };
    if (parts.length === 2) return { kind: "session", id: parts[1] };
    if (parts.length === 3 && SESSION_PROTECTED.has(parts[2])) return { refuse: "Capture and calendar data on a day belong to the job, so the box can't change them." };
    if (parts.length === 3 && (SESSION_TEXT[parts[2]] || ["date", "sessionType", "dayType", "isStudio"].includes(parts[2]))) return { kind: "sessionField", id: parts[1], field: parts[2] };
    return { refuse: `A day has no field called ${parts.slice(2).join(".")}.` };
  }
  if (head === "attachments") {
    if (parts.length === 3 && ATTACHMENT_PROTECTED.has(parts[2])) return { refuse: "Attachment files, addresses and extracted text can't be changed by the box." };
    if (parts.length === 3 && ["caption", "name", "includeInAddendum"].includes(parts[2])) return { kind: "attachmentField", id: parts[1], field: parts[2] };
    return { refuse: "Attachments can have their caption, name or print switch changed. Add or remove files in the builder." };
  }
  if (head === "printWording") {
    if (parts.length === 2 && PRINT_WORDING_KEYS.includes(parts[1])) return { kind: "wording", key: parts[1] };
    return { refuse: `The tool's own sentences are ${PRINT_WORDING_KEYS.join(", ")}. It has none called ${parts.slice(1).join(".") || "that"}.` };
  }
  if (head === "sections") {
    if (parts.length === 3 && SECTION_DEFS[parts[1]] && parts[2] === "text") return { kind: "sectionText", key: parts[1] };
    if (parts.length === 3 && SECTION_DEFS[parts[1]] && (parts[2] === "on" || parts[2] === "enabled")) return { kind: "sectionSwitch", key: parts[1] };
    return { refuse: `The quote has no section field called ${path}. Sections are ${SECTION_KEYS.join(", ")}, each with .text and .on.` };
  }
  const other = OTHER_KEYS[head];
  if (other) return { refuse: other[1] };
  return { refuse: `The quote has no field called ${path}.` };
}

/* ── One operation against a working copy ──────────────────────────
   w = { q, ids: { lines, discounts }, sessionIds, blockIds, attachmentIds, newCount, blockMoves }.
   Returns { refuse, label } or { label, target, reader, notes, ... }. `target` names what
   the operation changes, so two operations on one thing in a reply can be refused.
   `reader(q)` reads that thing back, so the preview can say whether the final quote still
   says what this operation asked. */

const wrongAction = (label, action, list) => (list.includes(action) ? null : { refuse: `${label} can't take the action "${action}". It takes ${list.join(", ")}.`, label });
const lineByKey = (qq, key) => (qq?.lineItems || []).find((x) => x.key === key) || null;

/** Billing adds or takes off the Pro bono row, so D2 has to go on meaning the same discount. */
function followDiscountIds(w, before) {
  const was = before?.quoteDiscounts || [];
  const now = w.q?.quoteDiscounts || [];
  if (now === was) return;
  w.ids.discounts = now.map((d, i) => {
    const k = was.indexOf(d);
    if (k >= 0) return w.ids.discounts[k];
    return was[i] && isProBonoDiscount(was[i]) && isProBonoDiscount(d) ? w.ids.discounts[i] : `new${++w.newCount}`;
  });
}

export function applyOperationToWorking(w, op, env) {
  const r = resolvePath(op.path);
  if (r.refuse) return { refuse: r.refuse, label: op.path };
  const { q } = w;
  const action = op.action || "set";
  switch (r.kind) {
    case "top": {
      const bad = wrongAction(r.entry.label, action, r.entry.actions);
      if (bad) return bad;
      const res = r.entry.apply(q, op.value, action, { ...env, replyStart: w.start });
      if (res.refuse) return { refuse: res.refuse, label: r.entry.label };
      w.q = { ...q, ...res.patch };
      followDiscountIds(w, q);
      return { label: r.entry.label, target: r.entry.path, reader: (qq) => r.entry.read(qq, env), notes: res.notes || [], movesClient: res.movesClient, discardDays: res.discardDays, noEffect: res.noEffect };
    }
    case "sectionText": {
      const label = `${SECTION_DEFS[r.key].label} wording`;
      const bad = wrongAction(label, action, ["set", "clear", "reset"]);
      if (bad) return bad;
      const res = applySectionText(q, r.key, op.value, action);
      if (res.refuse) return { refuse: res.refuse, label };
      w.q = { ...q, ...res.patch };
      return { label, target: `sections.${r.key}.text`, reader: (qq) => qq?.sections?.[r.key]?.text ?? "" };
    }
    case "sectionSwitch": {
      const label = `${SECTION_DEFS[r.key].label} section`;
      const bad = wrongAction(label, action, ["on", "off", "set"]);
      if (bad) return bad;
      const res = applySectionSwitch(q, r.key, op.value, action);
      if (res.refuse) return { refuse: res.refuse, label };
      w.q = { ...q, ...res.patch };
      return { label, target: `sections.${r.key}.on`, reader: (qq) => isSectionOn(qq, r.key) };
    }
    case "wording": {
      const label = PRINT_WORDING_LABELS[r.key];
      const bad = wrongAction(label, action, ["set", "clear", "reset"]);
      if (bad) return bad;
      const map = normalizePrintWording(q?.printWording);
      let next;
      let notes = [];
      if (action === "reset") next = null;
      else if (action === "clear") next = "";
      else {
        const t = asText(op.value, label);
        if (t.refuse) return { refuse: t.refuse, label };
        next = stripEmDashes(t.text).trim();
        if (next) {
          const check = checkWordingText(r.key, next, getDeposit(q).type);
          if (check.refuse) return { refuse: check.refuse, label };
          notes = check.notes || [];
        }
      }
      // Keeping the same payment wording after the money changed is a decision: it's been
      // read against the new amounts, so the warning about it clears.
      const moneyMoved = r.key === "paymentSentence" && q.printWordingMoneyKey && q.printWordingMoneyKey !== moneyFingerprint(q);
      if (next === wordingOf(q, r.key) && !moneyMoved) return { label, noEffect: `${label} already says that, so there is nothing to change.` };
      // The same words after the money moved change nothing in the map, so the runner would
      // call it no effect. The fingerprint moves here, and the runner stamps the final one.
      w.q = { ...q, printWording: { ...map, [r.key]: next }, ...(moneyMoved ? { printWordingMoneyKey: moneyFingerprint(q) } : {}) };
      return { label, target: `printWording.${r.key}`, reader: (qq) => wordingOf(qq, r.key), notes };
    }
    case "linesAdd": {
      if (action !== "add") return { refuse: `To change a line, name it, such as lines.L1.qty. "${action}" isn't an action on the line list.`, label: "New line" };
      const res = newLineFrom(op.value, q);
      if (res.refuse) return { refuse: res.refuse, label: "New line" };
      w.q = { ...q, lineItems: [...(q.lineItems || []), res.line] };
      w.ids.lines.push(`new${++w.newCount}`);
      return { label: `New line: ${res.line.description}`, target: "lines+", additive: true, reader: (qq) => !!lineByKey(qq, res.line.key), addedLine: res.line };
    }
    case "line": {
      const i = w.ids.lines.indexOf(r.id);
      if (i < 0) return { refuse: `This quote has no line ${r.id}.`, label: `Line ${r.id}` };
      const li = q.lineItems[i];
      const label = `Line: ${li.description || "untitled"}`;
      if (action !== "remove") return { refuse: `To change a line, name its field. "${action}" isn't an action on a whole line.`, label };
      if (isEstimatorCatalogId(li.catalogId)) return { refuse: FROZEN_LINE_REASON, label };
      w.q = { ...q, lineItems: q.lineItems.filter((_, k) => k !== i) };
      w.ids.lines.splice(i, 1);
      return { label, target: `lines.${r.id}`, reader: (qq) => !lineByKey(qq, li.key), removedLine: li };
    }
    case "lineField": {
      const i = w.ids.lines.indexOf(r.id);
      const f = LINE_FIELDS[r.field];
      if (i < 0) return { refuse: `This quote has no line ${r.id}.`, label: f.label };
      const li = q.lineItems[i];
      const label = `${f.label}, ${li.description || r.id}`;
      if (isEstimatorCatalogId(li.catalogId)) return { refuse: FROZEN_LINE_REASON, label };
      const bad = wrongAction(f.label, action, f.actions);
      if (bad) return { ...bad, label };
      const res = applyLinePatch(q, li, r.field, op.value, action);
      if (res.refuse) return { refuse: res.refuse, label };
      const merged = { ...li, ...res.line };
      if (r.field === "group" && !merged.group) delete merged.group;
      w.q = { ...q, lineItems: q.lineItems.map((x, k) => (k === i ? merged : x)) };
      const notes = res.line.rate !== undefined && r.field === "qty" && res.line.rate !== li.rate ? [`The rate follows the volume tier: $${li.rate} to $${res.line.rate}.`] : [];
      return { label, target: `lines.${r.id}.${r.field}`, lineKey: li.key, reader: (qq) => { const cur = lineByKey(qq, li.key); return cur ? f.read(cur) : null; }, notes };
    }
    case "discountsAdd": {
      if (action !== "add") return { refuse: `To change a discount, name it, such as discounts.D1.value. "${action}" isn't an action on the list.`, label: "New discount" };
      const extra = unknownKey(op.value, DISCOUNT_KEYS, "A new discount");
      if (extra) return { refuse: extra, label: "New discount" };
      const d = validDiscount(op.value, "Discount");
      if (d.refuse) return { refuse: d.refuse, label: "New discount" };
      if (!d.discount.name.trim()) return { refuse: "A quote discount needs a name, because the name prints.", label: "New discount" };
      if (isProBonoDiscount(d.discount)) return { refuse: PRO_BONO_DISCOUNT_REASON, label: "New discount" };
      const row = { key: newKey("qd"), ...d.discount };
      w.q = { ...q, quoteDiscounts: [...(q.quoteDiscounts || []), row] };
      w.ids.discounts.push(`new${++w.newCount}`);
      return { label: `New discount: ${row.name}`, target: "discounts+", additive: true, reader: (qq) => (qq?.quoteDiscounts || []).some((x) => x.key === row.key) };
    }
    case "discount":
    case "discountField": {
      const i = w.ids.discounts.indexOf(r.id);
      const d = q.quoteDiscounts?.[i];
      if (i < 0 || !d) return { refuse: `This quote has no discount ${r.id}.`, label: `Discount ${r.id}` };
      const label = r.kind === "discount" ? `Discount, ${d.name || r.id}` : `${DISCOUNT_FIELDS[r.field]}, ${d.name || r.id}`;
      if (isProBonoDiscount(d) && normalizeBilling(q?.billing) === "pro_bono") return { refuse: PRO_BONO_DISCOUNT_REASON, label };
      // A saved discount has no key (Ingest stores none). It gets one here so the preview can find its row.
      const key = d.key || newKey("qd");
      if (r.kind === "discount") {
        if (action !== "remove") return { refuse: `To change a discount, name its field. "${action}" isn't an action on a whole discount.`, label };
        w.q = { ...q, quoteDiscounts: q.quoteDiscounts.filter((_, k) => k !== i) };
        w.ids.discounts.splice(i, 1);
        return { label, target: `discounts.${r.id}`, reader: (qq) => !(qq?.quoteDiscounts || []).some((x) => (x.key || x) === (d.key || d)), removedDiscount: d };
      }
      const bad = wrongAction(label, action, ["set"]);
      if (bad) return bad;
      const check = validDiscount({ ...d, [r.field]: op.value }, "Discount");
      if (check.refuse) return { refuse: check.refuse, label };
      if (r.field === "name" && isProBonoDiscount({ name: op.value }) && !isProBonoDiscount(d)) return { refuse: PRO_BONO_DISCOUNT_REASON, label };
      const merged = { ...d, ...check.discount, key };
      w.q = { ...q, quoteDiscounts: q.quoteDiscounts.map((x, k) => (k === i ? merged : x)) };
      return { label, target: `discounts.${r.id}.${r.field}`, reader: (qq) => (qq?.quoteDiscounts || []).find((x) => x.key === key)?.[r.field] ?? null };
    }
    case "blocksAdd":
    case "block":
    case "blockField":
      return applyBlockOp(w, r, op, action);
    case "sessionsAdd":
    case "session":
    case "sessionField":
      return applySessionOp(w, r, op, action);
    case "attachmentField": {
      const a = (q.attachments || []).find((x) => x.id === w.attachmentIds[r.id]);
      const label = r.field === "includeInAddendum" ? "Attachment prints" : `Attachment ${r.field}`;
      if (!a) return { refuse: `This quote has no attachment ${r.id}.`, label };
      const badAction = wrongAction(label, action, r.field === "includeInAddendum" ? ["on", "off", "set"] : ["set"]);
      if (badAction) return badAction;
      let next;
      if (r.field === "includeInAddendum") {
        const b = asBool(op.value, action, label);
        if (b.refuse) return { refuse: b.refuse, label };
        if (b.bool && !isImageAttachment(a.type)) return { refuse: `${a.name || "This attachment"} can't print on the quote, because only pictures print. It stays attached.`, label };
        next = b.bool;
      } else {
        const t = asText(op.value, label);
        if (t.refuse) return { refuse: t.refuse, label };
        next = t.text;
      }
      w.q = { ...q, attachments: q.attachments.map((x) => (x.id === a.id ? { ...x, [r.field]: next } : x)) };
      w.attachmentEdits[a.id] = { ...(w.attachmentEdits[a.id] || {}), [r.field]: next };
      return { label: `${label}, ${a.name || r.id}`, target: `attachments.${r.id}.${r.field}`, reader: (qq) => (qq?.attachments || []).find((x) => x.id === a.id)?.[r.field] ?? null };
    }
    default:
      return { refuse: `The quote has no field called ${op.path}.`, label: op.path };
  }
}

function applyBlockOp(w, r, op, action) {
  const { q } = w;
  const groups = quoteLineGroups(q);
  const groupsPatch = (list) => ({ lineGroups: list, lineGroupNotes: lineGroupNotesFrom(list) });
  const blockOf = (qq, label) => quoteLineGroups(qq).find((g) => g.label === label) || null;
  if (r.kind === "blocksAdd") {
    if (action !== "add") return { refuse: `To change a block, name it, such as blocks.B1.note. "${action}" isn't an action on the list.`, label: "New block" };
    const extra = unknownKey(op.value, BLOCK_ADD_KEYS, "A new block");
    if (extra) return { refuse: extra, label: "New block" };
    const long = tooLong(op.value?.label ?? op.value, MAX_GROUP_LABEL, "A block name") || tooLong(op.value?.note, MAX_GROUP_NOTE, "A block note");
    if (long) return { refuse: long, label: "New block" };
    const label = cleanGroupLabel(op.value?.label ?? op.value);
    if (!label) return { refuse: "A new block needs a name.", label: "New block" };
    if (groups.some((g) => g.label === label)) return { noEffect: `The block ${label} is already on the quote.`, label: "New block" };
    const list = normalizeLineGroups([...groups, { label, note: op.value?.note ?? "" }]);
    w.q = { ...q, ...groupsPatch(list) };
    w.blockIds.push({ id: `new${++w.newCount}`, label });
    return { label: `New block: ${label}`, target: "blocks+", additive: true, reader: (qq) => !!blockOf(qq, label) };
  }
  const known = w.blockIds.find((b) => b.id === r.id);
  if (!known) return { refuse: `This quote has no block ${r.id}.`, label: `Block ${r.id}` };
  const group = groups.find((g) => g.label === known.label);
  if (!group) return { refuse: `Block ${known.label} is no longer on the quote.`, label: `Block ${known.label}` };
  if (r.kind === "block") {
    const label = `Block, ${group.label}`;
    if (action !== "remove") return { refuse: `To change a block, name its field. "${action}" isn't an action on a whole block.`, label };
    if ((q.lineItems || []).some((li) => cleanGroupLabel(li.group) === group.label && isEstimatorCatalogId(li.catalogId))) return { refuse: `${group.label} holds a LiDAR or drone line, so the heading stays. ${FROZEN_LINE_REASON}`, label };
    const lines = (q.lineItems || []).map((li) => (cleanGroupLabel(li.group) === group.label ? (({ group: _g, ...rest }) => rest)(li) : li));
    w.q = { ...q, lineItems: lines, ...groupsPatch(groups.filter((g) => g.label !== group.label)) };
    return { label, target: `blocks.${r.id}`, reader: (qq) => !blockOf(qq, group.label) };
  }
  const label = `${BLOCK_FIELDS[r.field]}, ${group.label}`;
  const badAction = wrongAction(label, action, { note: ["set", "clear"], printLabel: ["set", "clear", "reset"], order: ["set"] }[r.field]);
  if (badAction) return badAction;
  if (r.field === "note") {
    const t = action === "clear" ? { text: "" } : asText(op.value, label);
    if (t.refuse) return { refuse: t.refuse, label };
    const long = tooLong(t.text, MAX_GROUP_NOTE, label);
    if (long) return { refuse: long, label };
    const next = cleanGroupNote(t.text);
    w.q = { ...q, ...groupsPatch(groups.map((g) => (g.label === group.label ? { ...g, note: next } : g))) };
    return { label, target: `blocks.${r.id}.note`, reader: (qq) => blockOf(qq, group.label)?.note ?? null };
  }
  if (r.field === "printLabel") {
    const t = action === "clear" || action === "reset" ? { text: "" } : asText(op.value, label);
    if (t.refuse) return { refuse: t.refuse, label };
    const long = tooLong(t.text, MAX_GROUP_LABEL, label);
    if (long) return { refuse: long, label };
    const shown = cleanGroupLabel(t.text);
    const list = groups.map((g) => {
      if (g.label !== group.label) return g;
      const { printLabel: _old, ...rest } = g;
      return shown && shown !== group.label ? { ...rest, printLabel: shown } : rest;
    });
    w.q = { ...q, ...groupsPatch(list) };
    return { label, target: `blocks.${r.id}.printLabel`, reader: (qq) => { const g = blockOf(qq, group.label); return g ? g.printLabel || g.label : null; } };
  }
  // order
  const pos = Number(op.value);
  if (!Number.isInteger(pos) || pos < 1 || pos > groups.length) return { refuse: `Block order has to be a position from 1 to ${groups.length}.`, label };
  // Moves in one reply all mean their positions on the final page, so each one is placed
  // knowing the others.
  const taken = Object.entries(w.blockMoves || {}).find(([l, p]) => p === pos && l !== group.label);
  if (taken) return { refuse: `${taken[0]} is already moving to position ${pos} in this reply, so ${group.label} can't go there too.`, label };
  w.blockMoves = { ...w.blockMoves, [group.label]: pos };
  const without = groups.filter((g) => !(g.label in w.blockMoves));
  Object.entries(w.blockMoves).sort((a, b) => a[1] - b[1]).forEach(([l, p]) => {
    const moved = groups.find((g) => g.label === l);
    if (moved) without.splice(p - 1, 0, moved);
  });
  w.q = { ...q, ...groupsPatch(without) };
  return { label, target: `blocks.${r.id}.order`, reader: (qq) => { const at = quoteLineGroups(qq).findIndex((g) => g.label === group.label); return at < 0 ? null : at + 1; } };
}

function applySessionOp(w, r, op, action) {
  const { q } = w;
  if (!q.multiDay) return { refuse: "This is a single-day quote, so its date and place are scanDate, jobLocation and isStudio. Turn multiDay on to work with days.", label: "Day" };
  const sessions = readSchedule(q).days;
  const dayOf = (qq, id) => readSchedule(qq).days.find((s) => s.id === id) || null;
  if (r.kind === "sessionsAdd") {
    if (action !== "add") return { refuse: `To change a day, name it, such as sessions.S1.date. "${action}" isn't an action on the list.`, label: "New day" };
    const v = op.value && typeof op.value === "object" ? op.value : {};
    // An empty date is a day still to be scheduled, as sessions.S1.date clear leaves one.
    const d = v.date === "" ? { date: "" } : asDate(v.date, "A new day's date");
    if (d.refuse) return { refuse: d.refuse, label: "New day" };
    const unknown = Object.keys(v).find((k) => !["date", "sessionType", "dayType", "isStudio"].includes(k) && !SESSION_TEXT[k]);
    if (unknown) return { refuse: `A new day can't take ${unknown}.`, label: "New day" };
    const sessionType = v.sessionType ?? "capture";
    if (!SESSION_TYPES.includes(sessionType)) return { refuse: `Day type can be ${SESSION_TYPES.join(", ")}, not "${sessionType}".`, label: "New day" };
    const dayType = dayTypeOf(v.dayType ?? "full");
    if (!DAY_TYPES.includes(dayType)) return { refuse: `A day can be ${DAY_TYPES.join(" or ")}, not "${v.dayType}".`, label: "New day" };
    const studio = v.isStudio === undefined ? { bool: false } : asBool(v.isStudio, "set", "Day at the studio");
    if (studio.refuse) return { refuse: studio.refuse, label: "New day" };
    const typed = {};
    for (const k of Object.keys(SESSION_TEXT)) {
      if (v[k] === undefined || v[k] === null) continue;
      const t = SESSION_TIMES.has(k) ? asTime(v[k], SESSION_TEXT[k]) : asText(v[k], SESSION_TEXT[k]);
      if (t.refuse) return { refuse: t.refuse, label: "New day" };
      typed[k] = t.text;
    }
    const clash = studio.bool && ["location", ...ADDRESS_PARTS].find((k) => typed[k]);
    if (clash) return { refuse: `A day at the Studio has the studio's address, so ${SESSION_TEXT[clash]} can't go on it.`, label: "New day" };
    const candidate = { ...BLANK_SESSION(), ...typed, sessionType, dayType, date: d.date, ...(studio.bool ? studioPatch() : {}) };
    // Two days clash only on a date, so a day with none has nothing to check.
    const check = d.date ? validateSessionAdd({ sessions }, candidate) : { ok: true };
    if (!check.ok) return { refuse: check.reason, label: "New day" };
    const res = writeSchedule(q, { sessions: [...sessions, candidate] });
    if (res.refuse) return { refuse: res.refuse, label: "New day" };
    w.q = { ...q, ...res.patch };
    w.sessionIds.push({ id: `new${++w.newCount}`, sessionId: candidate.id });
    return { label: `New day: ${d.date || "no date yet"}`, target: "sessions+", additive: true, reader: (qq) => !!dayOf(qq, candidate.id) };
  }
  const known = w.sessionIds.find((s) => s.id === r.id);
  const session = known && sessions.find((s) => s.id === known.sessionId);
  if (!session) return { refuse: `This quote has no day ${r.id}.`, label: `Day ${r.id}` };
  const dayName = session.date || r.id;
  if (r.kind === "session") {
    const label = `Day, ${dayName}`;
    if (action !== "remove") return { refuse: `To change a day, name its field. "${action}" isn't an action on a whole day.`, label };
    if (sessionIsProtected(session)) return { refuse: "This day has capture data or a calendar event, so it can't be removed here.", label };
    const res = writeSchedule(q, { removeSession: session.id });
    if (res.refuse) return { refuse: res.refuse, label };
    w.q = { ...q, ...res.patch };
    return { label, target: `sessions.${r.id}`, reader: (qq) => !dayOf(qq, session.id), discardDays: [session] };
  }
  const { field } = r;
  const [labelText, actions] = field === "date" ? ["Day date", ["set", "clear"]] : field === "sessionType" ? ["Day type", ["set"]] : field === "dayType" ? ["Full or partial day", ["set"]]
    : field === "isStudio" ? ["Day at the studio", ["on", "off", "set"]] : [SESSION_TEXT[field], ["set", "clear"]];
  const badAction = wrongAction(labelText, action, actions);
  if (badAction) return badAction;
  let change;
  if (field === "date") {
    if (action === "clear") change = { updateSession: { id: session.id, patch: { date: "" } } };
    else {
      const d = asDate(op.value, labelText);
      if (d.refuse) return { refuse: d.refuse, label: labelText };
      change = { updateSession: { id: session.id, patch: { date: d.date } } };
    }
  } else if (field === "sessionType") {
    if (!SESSION_TYPES.includes(op.value)) return { refuse: `Day type can be ${SESSION_TYPES.join(", ")}, not "${op.value}".`, label: labelText };
    change = { updateSession: { id: session.id, patch: { sessionType: op.value } } };
  } else if (field === "dayType") {
    const dayType = dayTypeOf(op.value);
    if (!DAY_TYPES.includes(dayType)) return { refuse: `A day can be ${DAY_TYPES.join(" or ")}, not "${op.value}".`, label: labelText };
    change = { updateSession: { id: session.id, patch: { dayType } } };
  } else if (field === "isStudio") {
    const b = asBool(op.value, action, labelText);
    if (b.refuse) return { refuse: b.refuse, label: labelText };
    if (b.bool === !!session.isStudio) return { noEffect: `This day is already ${b.bool ? "at" : "away from"} the Studio.`, label: labelText };
    change = { toggleSessionStudio: session.id };
  } else {
    if (session.isStudio && ADDRESS_PARTS.includes(field)) return { refuse: `${labelText} can't change while this day is at the Studio, because it holds the studio's address. Change the day's location first.`, label: labelText };
    const t = action === "clear" ? { text: "" } : (SESSION_TIMES.has(field) ? asTime(op.value, labelText) : asText(op.value, labelText));
    if (t.refuse) return { refuse: t.refuse, label: labelText };
    // Leaving the Studio takes its address with it, or the job would still map to it.
    const patch = field === "location" ? { ...(session.isStudio ? clearLocationPatch() : {}), location: t.text, isStudio: false } : { [field]: t.text };
    change = { updateSession: { id: session.id, patch } };
  }
  const res = writeSchedule(q, change);
  if (res.refuse) return { refuse: res.refuse, label: labelText };
  w.q = { ...q, ...res.patch };
  return { label: `${labelText}, ${dayName}`, target: `sessions.${r.id}.${field}`, reader: (qq) => dayOf(qq, session.id)?.[field] ?? null };
}
